import { useMemo } from 'react'
import {
  HStack,
  VStack,
  Text,
  Box} from '@chakra-ui/react'
import cx from 'classnames'
import useBreakPoint from 'hooks/useBreakPoint'
import { observer } from 'mobx-react'
import Highlighter from 'react-highlight-words'
import { Col } from 'reactstrap'
import { EBreakPoint } from 'constants/theme'
import { IMedia } from 'interfaces/media'
import { IStepWithRelations } from 'interfaces/step'
import IconBuilder from 'pages/IconBuilderPage/components/IconBuilder'
import styles from './cardHeader.module.scss'

interface ICardHeaderProps {
  name?: string
  position?: number
  onClick: () => void
  mediaList: IMedia[]
  step: IStepWithRelations
  lastStep: boolean
  procedureId: number
  className?: string
  searchText?: any
  isCollapsed: boolean
  hasBlocksToReview?: boolean
}

const CardHeader = ({
  name,
  position,
  searchText,
  step,
  onClick,
  lastStep,
  procedureId,
  className,
  isCollapsed,
  hasBlocksToReview = false,
  ...props
}: ICardHeaderProps) => {
  const isMobile: boolean = useBreakPoint(EBreakPoint.BASE, EBreakPoint.MD)

  const contents: string = useMemo(() => {
    return Array.isArray(step?.textContents) ? step.textContents.map(content => content?.content ?? '').join(' ') : ''
  }, [step.textContents])

  const searchIndex: number = String(searchText)
    .toLowerCase()
    .split(' ')
    .reduce((result, currentValue) => {
      const index: number = contents.toLowerCase().indexOf(currentValue)
      if (index > -1 && (index < result || result < 0)) {
        return index
      }
      return result
    }, -1)

  const textToHighlight: string = useMemo(() => {
    const maxStart: number = [searchIndex - 10, searchIndex - 20, searchIndex - 30, searchIndex, 0].reduce(
      (result, currentValue) => {
        if (currentValue > 0 && currentValue < result && currentValue < searchIndex) {
          return currentValue
        }
        return result
      },
      searchIndex
    )
    const sliceStart: number = contents.slice(maxStart, searchIndex).indexOf(' ') + maxStart + 1
    const sliceEnd: number =
      contents.slice(sliceStart + Math.max(60, contents.length - sliceStart)).indexOf(' ') + sliceStart + 60
    //* INFO: replace any html tags that might be in the text

    return contents.slice(sliceStart, sliceEnd).replace(/<[^>]*>?/gm, ' ')
  }, [contents, searchIndex, searchText])




  return (
    <Col
      xl="12"
      lg="12"
      md="12"
      sm="12"
      xs="12"
      className={cx(
        styles.container,
        className
      )}
      onClick={onClick}
      style={{
        backgroundColor: hasBlocksToReview ? '#FEF2F2' : 'white'
      }}
    >
      <VStack width="full" align="flex-start" paddingX={{ base: 2, md: 0 }}>
        <HStack width="full">
          <div className={styles.layout}>
            <div className={styles.stepIcon}>
              {step.icon && <IconBuilder icon={step.icon} size={32} isActive />}
            </div>
            <div className={styles.label}>
              {!isMobile && (
                <Box
                  flexDirection="row"
                  display="flex"
                >
                  <Text margin={0} alignSelf="center">
                    {position}.
                  </Text>
                  <Text margin={0}>
                      {name}
                    </Text>
                </Box>
              )}

              {searchText && isCollapsed && searchIndex > -1 && (
                <VStack>
                  <Highlighter
                    class={cx(styles.title)}
                    highlightClassName={styles.highlight}
                    searchWords={[
                      ...String(searchText).toUpperCase().split(' '),
                      ...String(searchText).toLowerCase().split(' ')
                    ]}
                    autoEscape
                    textToHighlight={textToHighlight}
                  />
                </VStack>
              )}
            </div>
          </div>
        </HStack>
        {isMobile && (
          <Box
            flexDirection="row"
            display="flex"
            paddingBottom={2}
          >
            
              <Text
                color="gray.700"
                fontWeight={500}
                lineHeight={5}
                margin={0}
              >
                {`${position}. ${name}`}
              </Text>
          </Box>
        )}
      </VStack>

    </Col>
  )
}

export default observer(CardHeader)
